import DataLoadingModal from "@/Components/UI/DataLoadingModal";
import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import SecondaryButton from "@/Components/UI/Forms/SecondaryButton";
import { Client, None, PageProps } from "@/types/page";
import { Price } from "@/types/price";
import { formatPrice } from "@/utils/moneyFormatting";
import { prettyPriceInterval } from "@/utils/time";
import { ActiveDiscountData } from "@/utils/useActiveDiscountData";
import { router, useForm } from "@inertiajs/react";
import { format, isPast } from "date-fns";
import { useMemo, useState } from "react";

type ConfirmResumeSubscriptionModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export type ResumeSubscriptionData = {
    subscriptionEndsAt: string;
    financialAssistanceData: ActiveDiscountData | null;
    price: Price;
};

type ResumeSubscriptionPageProps = PageProps<None, Client>;

export default function ConfirmResumeSubscriptionModal({
    isOpen,
    onClose,
}: ConfirmResumeSubscriptionModalProps) {
    const [modalData, setModalData] = useState<ResumeSubscriptionData>();

    const inGracePeriod = modalData && !isPast(modalData.subscriptionEndsAt);

    const { post, processing } = useForm();
    const onConfirm = () => {
        post(route("subscription.resume"), {
            onSuccess: (e) => {
                const flash = (
                    e.props as unknown as ResumeSubscriptionPageProps
                ).flash;
                if (flash && flash.message === "payment_incomplete") {
                    router.visit(route("payment-method"));
                }
                onClose();
            },
        });
    };

    const additionalDiscountText = useMemo(() => {
        if (!modalData?.financialAssistanceData) {
            return "";
        }
        return ` (financial assistance discount applied, expires ${format(modalData.financialAssistanceData.endAt, "MMMM dd, y")})`;
    }, [modalData?.financialAssistanceData]);

    const originalPriceToDisplay = useMemo(() => {
        if (!modalData?.price) {
            return "$0";
        }
        return formatPrice(modalData.price);
    }, [modalData?.price]);

    const priceToDisplay = useMemo(
        () =>
            modalData?.price
                ? formatPrice(
                      modalData.price,
                      modalData.financialAssistanceData?.coupon,
                  )
                : "$0",
        [modalData?.price, modalData?.financialAssistanceData?.coupon],
    );

    const hasDiscount = modalData?.financialAssistanceData?.coupon;

    const priceDisplayElement = useMemo(() => {
        if (!hasDiscount) {
            return priceToDisplay;
        }
        return (
            <>
                <span className="line-through">{originalPriceToDisplay}</span>{" "}
                {priceToDisplay}
            </>
        );
    }, [hasDiscount, originalPriceToDisplay, priceToDisplay]);

    return (
        <DataLoadingModal
            id="confirm-resume-subscription-modal"
            route={route("modals.getConfirmResumeSubscriptionData")}
            show={isOpen}
            onClose={onClose}
            modalData={modalData}
            finishedLoadingCallback={setModalData}
            animateClose={false}
        >
            <div
                data-testid="resume-coaching-modal"
                className="flex h-full max-h-[80vh] flex-col items-stretch p-6 sm:p-8"
            >
                {modalData && modalData.price && (
                    <>
                        <div
                            data-testid="coaching-renewal-heading"
                            className="pb-4 text-left text-2xl font-bold"
                        >
                            Resume coaching
                        </div>
                        <div data-testid="plan-renewal-description">
                            Your coaching plan will{" "}
                            {inGracePeriod
                                ? `renew on ${format(modalData.subscriptionEndsAt, "MMMM dd, y")}`
                                : "resume immediately"}{" "}
                            and you will be billed {priceDisplayElement} every{" "}
                            {prettyPriceInterval(modalData.price)} until you
                            cancel{additionalDiscountText}. You can change
                            coaches or cancel your membership at any time from
                            your account settings page.
                        </div>
                        <div className="flex flex-col justify-end gap-3 pt-6 sm:flex-row">
                            <PrimaryButton
                                onClick={onConfirm}
                                disabled={processing}
                                data-testid="resume-coaching-button"
                                className={
                                    "grow justify-center py-3 sm:order-2 sm:w-0"
                                }
                            >
                                Resume coaching
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={onClose}
                                data-testid="go-back-button"
                                className={"grow justify-center py-3 sm:w-0"}
                            >
                                Go back
                            </SecondaryButton>
                        </div>
                    </>
                )}
            </div>
        </DataLoadingModal>
    );
}
